import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Instagram, Facebook, Send, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { api } from "../../services/api";

const eventTypes = [
    "Koncert",
    "Firemná akcia",
    "Festival",
    "Konferencia",
    "Svadba",
    "Kultúrne podujatie",
    "Súkromná oslava",
    "Iné",
];

const DEFAULT_INFO = { phone: "+421 905 000 000", email: "info@kbmusic.sk", area: "Celá SR & ČR", address: "", city: "", facebook_url: "", instagram_url: "", recaptcha_site_key: "" };

export default function Contact() {
    const [info, setInfo] = useState(DEFAULT_INFO);
    useEffect(() => { api.getSettings().then(d => setInfo(s => ({ ...s, ...d }))).catch(() => {}); }, []);

    useEffect(() => {
        if (!info.recaptcha_site_key) return;
        if (document.querySelector(`script[data-recaptcha]`)) return;
        const s = document.createElement("script");
        s.src = `https://www.google.com/recaptcha/api.js?render=${info.recaptcha_site_key}`;
        s.async = true;
        s.setAttribute("data-recaptcha", "1");
        document.head.appendChild(s);
    }, [info.recaptcha_site_key]);

    const getRecaptchaToken = () => {
        if (!info.recaptcha_site_key || !window.grecaptcha) return Promise.resolve("");
        return new Promise(resolve => {
            window.grecaptcha.ready(() => {
                window.grecaptcha.execute(info.recaptcha_site_key, { action: "contact" })
                    .then(resolve).catch(() => resolve(""));
            });
        });
    };

    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        eventType: "",
        eventDate: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Vyplňte prosím povinné polia (meno, email, správa).");
            return;
        }
        setSubmitting(true);
        try {
            const recaptcha_token = await getRecaptchaToken();
            await api.sendContact({
                name:            form.name,
                email:           form.email,
                phone:           form.phone,
                event_type:      form.eventType,
                event_date:      form.eventDate,
                message:         form.message,
                recaptcha_token,
            });
            setDone(true);
            toast.success("Ďakujeme! Ozveme sa vám do 24 hodín.");
            setForm({ name: "", email: "", phone: "", eventType: "", eventDate: "", message: "" });
            setTimeout(() => setDone(false), 5000);
        } catch (err) {
            toast.error(err.message || "Odoslanie zlyhalo. Skúste neskôr.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="contact"
            className="relative py-24 sm:py-32 px-6 sm:px-12 bg-nova-black overflow-hidden"
            data-testid="contact-section"
        >
            <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-nova-cyan/10 rounded-full blur-[140px] pointer-events-none" />

            <div className="relative max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                    {/* Left info */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-5"
                    >
                        <div className="font-mono text-xs uppercase tracking-[0.25em] text-nova-cyan mb-4">
                            / 07 — Kontakt
                        </div>
                        <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter leading-[1.05]">
                            Poďme spolu vytvoriť{" "}
                            <span className="text-nova-cyan">nezabudnuteľný</span>{" "}
                            event
                        </h2>
                        <p className="mt-6 text-zinc-400 text-lg leading-relaxed">
                            Sme pripravení odpovedať na vaše otázky a pripraviť
                            pre vás technické riešenie na mieru.
                        </p>

                        <div className="mt-12 space-y-6">
                            {info.phone && (
                                <a href={`tel:${info.phone.replace(/\s/g,"")}`} className="flex items-start gap-4 group" data-testid="contact-phone">
                                    <div className="w-10 h-10 rounded-md bg-nova-cyan/8 border border-nova-cyan/20 flex items-center justify-center shrink-0 group-hover:border-nova-cyan/50 transition-colors">
                                        <Phone className="w-4 h-4 text-nova-cyan" />
                                    </div>
                                    <div>
                                        <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">Telefón</div>
                                        <div className="text-white font-medium mt-1 group-hover:text-nova-cyan transition-colors">{info.phone}</div>
                                    </div>
                                </a>
                            )}

                            {info.email && (
                                <a href={`mailto:${info.email}`} className="flex items-start gap-4 group" data-testid="contact-email">
                                    <div className="w-10 h-10 rounded-md bg-nova-purple/8 border border-nova-purple/20 flex items-center justify-center shrink-0 group-hover:border-nova-purple/50 transition-colors">
                                        <Mail className="w-4 h-4 text-nova-purple" />
                                    </div>
                                    <div>
                                        <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">Email</div>
                                        <div className="text-white font-medium mt-1 group-hover:text-nova-purple transition-colors">{info.email}</div>
                                    </div>
                                </a>
                            )}

                            {(info.area || info.city) && (
                                <div className="flex items-start gap-4" data-testid="contact-location">
                                    <div className="w-10 h-10 rounded-md bg-nova-cyan/8 border border-nova-cyan/20 flex items-center justify-center shrink-0">
                                        <MapPin className="w-4 h-4 text-nova-cyan" />
                                    </div>
                                    <div>
                                        <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">Pôsobnosť</div>
                                        <div className="text-white font-medium mt-1">
                                            {info.area}{info.city ? `, sídlo: ${info.city}` : ""}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {(info.instagram_url || info.facebook_url) && (
                        <div className="mt-10 pt-10 border-t border-white/10">
                            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-4">
                                Sociálne siete
                            </div>
                            <div className="flex gap-3">
                                {info.instagram_url && (
                                    <a href={info.instagram_url} target="_blank" rel="noopener noreferrer"
                                        className="w-11 h-11 rounded-full border border-white/15 bg-nova-surface/60 hover:bg-nova-surface hover:border-nova-cyan/50 flex items-center justify-center transition-all"
                                        aria-label="Instagram" data-testid="social-instagram">
                                        <Instagram className="w-4 h-4 text-white" />
                                    </a>
                                )}
                                {info.facebook_url && (
                                    <a href={info.facebook_url} target="_blank" rel="noopener noreferrer"
                                        className="w-11 h-11 rounded-full border border-white/15 bg-nova-surface/60 hover:bg-nova-surface hover:border-nova-purple/50 flex items-center justify-center transition-all"
                                        aria-label="Facebook" data-testid="social-facebook">
                                        <Facebook className="w-4 h-4 text-white" />
                                    </a>
                                )}
                            </div>
                        </div>
                        )}
                    </motion.div>

                    {/* Form */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="lg:col-span-7"
                    >
                        <form
                            onSubmit={submit}
                            className="border border-white/8 bg-nova-surface/60 rounded-xl p-8 sm:p-10 space-y-5"
                            data-testid="contact-form"
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div className="space-y-2">
                                    <Label htmlFor="name" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">
                                        Meno *
                                    </Label>
                                    <Input
                                        id="name"
                                        value={form.name}
                                        onChange={(e) => update("name", e.target.value)}
                                        placeholder="Ján Novák"
                                        className="h-12 bg-nova-black border-white/10 text-white placeholder:text-zinc-600 focus:border-nova-cyan focus-visible:ring-nova-cyan/40"
                                        data-testid="contact-input-name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">
                                        E-mail *
                                    </Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => update("email", e.target.value)}
                                        placeholder="jan@firma.sk"
                                        className="h-12 bg-nova-black border-white/10 text-white placeholder:text-zinc-600 focus:border-nova-cyan focus-visible:ring-nova-cyan/40"
                                        data-testid="contact-input-email"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div className="space-y-2">
                                    <Label htmlFor="phone" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">
                                        Telefón
                                    </Label>
                                    <Input
                                        id="phone"
                                        value={form.phone}
                                        onChange={(e) => update("phone", e.target.value)}
                                        placeholder="+421 ..."
                                        className="h-12 bg-nova-black border-white/10 text-white placeholder:text-zinc-600 focus:border-nova-cyan focus-visible:ring-nova-cyan/40"
                                        data-testid="contact-input-phone"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="eventDate" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">
                                        Dátum podujatia
                                    </Label>
                                    <Input
                                        id="eventDate"
                                        type="date"
                                        value={form.eventDate}
                                        onChange={(e) => update("eventDate", e.target.value)}
                                        className="h-12 bg-nova-black border-white/10 text-white placeholder:text-zinc-600 focus:border-nova-cyan focus-visible:ring-nova-cyan/40 [color-scheme:dark]"
                                        data-testid="contact-input-date"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-zinc-400 text-xs font-mono uppercase tracking-wider">
                                    Typ podujatia
                                </Label>
                                <Select
                                    value={form.eventType}
                                    onValueChange={(v) => update("eventType", v)}
                                >
                                    <SelectTrigger
                                        className="h-12 bg-nova-black border-white/10 text-white focus:border-nova-cyan focus:ring-nova-cyan/40"
                                        data-testid="contact-select-type"
                                    >
                                        <SelectValue placeholder="Vyberte typ podujatia" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-nova-surface border-white/10 text-white">
                                        {eventTypes.map((t) => (
                                            <SelectItem
                                                key={t}
                                                value={t}
                                                className="focus:bg-nova-cyan/20 focus:text-white"
                                                data-testid={`contact-option-${t}`}
                                            >
                                                {t}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="message" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">
                                    Správa *
                                </Label>
                                <Textarea
                                    id="message"
                                    rows={5}
                                    value={form.message}
                                    onChange={(e) => update("message", e.target.value)}
                                    placeholder="Napíšte nám o vašom podujatí..."
                                    className="bg-nova-black border-white/10 text-white placeholder:text-zinc-600 focus:border-nova-cyan focus-visible:ring-nova-cyan/40 resize-none"
                                    data-testid="contact-input-message"
                                />
                            </div>

                            <Button
                                type="submit"
                                disabled={submitting || done}
                                className="w-full h-12 rounded-md bg-nova-cyan text-black hover:bg-nova-cyan hover:saturate-125 font-semibold text-base transition-all hover:shadow-[0_0_25px_rgba(0,153,255,0.35)] disabled:opacity-80"
                                data-testid="contact-submit-button"
                            >
                                {done ? (
                                    <>
                                        <Check className="mr-2 w-5 h-5" />
                                        Odoslané — ďakujeme!
                                    </>
                                ) : submitting ? (
                                    <>Odosielam...</>
                                ) : (
                                    <>
                                        <Send className="mr-2 w-5 h-5" />
                                        Odoslať dopyt
                                    </>
                                )}
                            </Button>

                            <p className="text-xs text-zinc-500 text-center pt-2">
                                Odoslaním súhlasíte so spracovaním osobných
                                údajov pre účely vybavenia vašej požiadavky.
                            </p>
                        </form>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
